import Typography, { ETypo } from "@Front/Components/DesignSystem/Typography";
import DefaultLegalDashboard, { ELegalOptions } from "@Front/Components/LayoutTemplates/DefaultLegalDashboard";
import { DeploymentLineLegalPublisherProvider } from "@Front/Services/DeploymentLineLegalPublisher/DeploymentLineLegalPublisherContext";
import { usePublicLegalStaticFromSiteConfig } from "@Front/Services/PublicSiteConfig/usePublicLegalStaticFromSiteConfig";
import Link from "next/link";
import { useMemo } from "react";
import { useRouter } from "next/router";

import CGU from "./CGU";
import classes from "./classes.module.scss";
import LegalNotice from "./LegalNotice";
import PrivacyPolicy from "./PrivacyPolicy";

export default function LegalInformations(): React.JSX.Element {
	const router = useRouter();
	const publicLegal = usePublicLegalStaticFromSiteConfig();
	const pdfLinks = useMemo<Record<ELegalOptions, string>>(
		() => ({
			"mentions-legales": publicLegal.documentPdfPaths.legalMentions,
			"politique-de-confidentialite": publicLegal.documentPdfPaths.confidentiality,
			"politique-de-gestion-des-cookies": publicLegal.documentPdfPaths.cookies,
			cgs: publicLegal.documentPdfPaths.cgs,
			cgu: publicLegal.documentPdfPaths.cgu,
		}),
		[publicLegal.documentPdfPaths],
	);
	const rawLegalUid = router.query["legalUid"];
	const legalUid = typeof rawLegalUid === "string" ? rawLegalUid : undefined;

	if (!router.isReady || !legalUid) {
		return (
			<DeploymentLineLegalPublisherProvider>
				<DefaultLegalDashboard>
					<Typography typo={ETypo.TEXT_MD_REGULAR}>Chargement…</Typography>
				</DefaultLegalDashboard>
			</DeploymentLineLegalPublisherProvider>
		);
	}

	const legalType = legalUid as ELegalOptions;
	return (
		<DeploymentLineLegalPublisherProvider>
			<DefaultLegalDashboard>{getLegalInformationContent(legalType, pdfLinks)}</DefaultLegalDashboard>
		</DeploymentLineLegalPublisherProvider>
	);
}

function getLegalInformationContent(legalType: ELegalOptions, pdfLinks: Record<ELegalOptions, string>): React.ReactNode | null {
	switch (legalType) {
		case ELegalOptions.LEGAL_MENTIONS:
			return <LegalNotice />;
		case ELegalOptions.CGU:
			return <CGU />;
		case ELegalOptions.CGS:
			return (
				<object data={pdfLinks[legalType]} type="application/pdf" width="100%" height="100%" className={classes["pdf-viewer"]}>
					<Typography typo={ETypo.TEXT_LG_LIGHT} className={classes["no-pdf-text"]}>
						Votre navigateur ne prend pas en charge l&apos;affichage des pdf&nbsp;
						<Typography typo={ETypo.TEXT_LG_LIGHT} type="span">
							<Link href={pdfLinks[legalType]} target="_blank" className={classes["pdf-fallback-link"]}>
								cliquez ici pour télécharger le pdf.
							</Link>
						</Typography>
					</Typography>
				</object>
			);
		case ELegalOptions.POLITIQUE_DE_CONFIDENTIALITE:
			return <PrivacyPolicy />;
		case ELegalOptions.POLITIQUE_DE_GESTION_DES_COOKIES:
			return (
				<object data={pdfLinks[legalType]} type="application/pdf" width="100%" height="100%" className={classes["pdf-viewer"]}>
					<Typography typo={ETypo.TEXT_LG_LIGHT} className={classes["no-pdf-text"]}>
						Votre navigateur ne prend pas en charge l&apos;affichage des pdf&nbsp;
						<Typography typo={ETypo.TEXT_LG_LIGHT} type="span">
							<Link href={pdfLinks[legalType]} target="_blank" className={classes["pdf-fallback-link"]}>
								cliquez ici pour télécharger le pdf.
							</Link>
						</Typography>
					</Typography>
				</object>
			);
		default:
			return null;
	}
}
